import { useMemo } from "react";
import Card from "@rio-cloud/rio-uikit/Card";
import Map from "@rio-cloud/rio-uikit/Map";
import MarkerLayer from "@rio-cloud/rio-uikit/MarkerLayer";
import SingleMapMarker from "@rio-cloud/rio-uikit/SingleMapMarker";
import ClusterMapMarker from "@rio-cloud/rio-uikit/ClusterMapMarker";
import MapSettings from "@rio-cloud/rio-uikit/MapSettings";
import MapTypeSettings from "@rio-cloud/rio-uikit/MapTypeSettings";
import type { TollMapPin } from "@services/tolls";
import { useT } from "@hooks";

const API_KEY = process.env.REACT_APP_RIO_MAP_KEY || "";

const BRAZIL_CENTER = { lat: -15.78, lng: -47.93 };

type VolumeColor = "danger" | "warning" | "success";

const VOLUME_LINE_COLORS: Record<VolumeColor, string> = {
  danger: "#D90000",
  warning: "#FF8E3C",
  success: "#5CB85C",
};

const MARKER_COLOR_CLASS: Record<VolumeColor, string> = {
  danger: "bg-map-marker-danger",
  warning: "bg-map-marker-warning",
  success: "bg-map-marker-success",
};

const getVolumeColor = (passages: number): VolumeColor => {
  if (passages >= 100) return "danger";
  if (passages >= 31) return "warning";
  return "success";
};

const VolumeIndicator = ({ color }: { color: string }) => (
  <span style={{ width: 22, height: 0, borderTop: `3px solid ${color}` }} />
);

/**
 * Tooltip à direita do pin, em CSS puro. O marcador é renderizado como HTML
 * estático (renderToStaticMarkup), então o <Tooltip>/<OverlayTrigger> do
 * rio-uikit não funcionam dentro dele (dependem de eventos/refs do React).
 *
 * - top: -30px + translateY(-50%): a origem do marcador fica na âncora (base do
 *   pin); o balão "md" fica ~30px acima (margin-bottom 15px + ~31px de altura),
 *   então centralizamos o tooltip verticalmente no balão.
 * - :has(): no hover, sobe o container do marcador (HERE) acima dos demais pins,
 *   já que cada marcador é um contexto de empilhamento próprio. Degrada de forma
 *   segura em navegadores sem :has() (o tooltip apenas não vence o z-index).
 */
const MAP_TIP_STYLE = `
.toll-pin-tip { position: relative; display: inline-block; }
/* O HERE define z-index inline em cada marcador (um contexto de empilhamento por pin).
   Para o tooltip ficar SEMPRE à frente, no hover elevamos ao máximo (com !important,
   para vencer o inline) tanto o próprio pin quanto TODOS os seus ancestrais — assim o
   marcador inteiro sobe acima dos demais, independente de quantos níveis o HERE aninhe.
   Só afeta ancestrais posicionados (os <div> estáticos da página ignoram z-index). */
.toll-pin-tip:hover { z-index: 2147483647 !important; }
*:has(.toll-pin-tip:hover) { z-index: 2147483647 !important; }
.toll-pin-tip__box {
  position: absolute;
  top: -30px;
  left: 100%;
  transform: translateY(-50%);
  margin-left: 40px;
  display: flex;
  flex-direction: column;
  gap: 2px;
  white-space: nowrap;
  background: #25292d;
  color: #fff;
  padding: 6px 10px;
  border-radius: 4px;
  font-size: 12px;
  line-height: 1.5;
  box-shadow: 0 2px 8px rgba(0, 0, 0, 0.3);
  opacity: 0;
  pointer-events: none;
  transition: opacity .12s ease-in-out;
  z-index: 2147483647;
}
.toll-pin-tip__box::before {
  content: "";
  position: absolute;
  top: 50%;
  right: 100%;
  transform: translateY(-50%);
  border: 6px solid transparent;
  border-right-color: #25292d;
}
.toll-pin-tip:hover .toll-pin-tip__box { opacity: 1; }
`;

/**
 * Praças que compartilham exatamente a mesma lat/long são agrupadas em um único
 * ponto: somamos as passagens (totalPassages) e mantemos a lista de praças
 * (entries) para montar o tooltip com uma linha por praça.
 */
type AggregatedPin = {
  lat: number;
  lng: number;
  totalPassages: number;
  entries: TollMapPin[];
};

type Translate = ReturnType<typeof useT>;

const createSimpleTheme = (t: Translate) => ({
  getNoisePresentation: (point: AggregatedPin) => {
    const volumeColor = getVolumeColor(point.totalPassages);
    return (
      <span className="toll-pin-tip">
        <SingleMapMarker
          markerColor={MARKER_COLOR_CLASS[volumeColor] as any}
          iconNames={["rioglyph rioglyph-poi"]}
          name={String(point.totalPassages)}
          anchorSize="md"
        />
        <span className="toll-pin-tip__box">
          {point.entries.map((entry, index) => (
            <span key={`${entry.plazaId}-${index}`} className="toll-pin-tip__line">
              {t("tolls.map.passagesEntry", { passages: entry.passages, plaza: entry.plazaName })}
            </span>
          ))}
        </span>
      </span>
    );
  },
  getClusterPresentation: (points: AggregatedPin[]) => {
    const totalPassages = points.reduce((sum, p) => sum + (p.totalPassages || 0), 0);
    const volumeColor = getVolumeColor(totalPassages);
    return <ClusterMapMarker count={points.length} markerColor={MARKER_COLOR_CLASS[volumeColor] as any} />;
  },
});

interface Props {
  pins?: TollMapPin[];
}

export default function TollConcentrationMapCard({ pins = [] }: Readonly<Props>) {
  const t = useT();

  const VOLUME_LEGEND: { color: VolumeColor; label: string }[] = [
    { color: "danger", label: t("tolls.map.volumeHigh") },
    { color: "warning", label: t("tolls.map.volumeMedium") },
    { color: "success", label: t("tolls.map.volumeLow") },
  ];

  // Agrupa por lat/long exatos, somando as passagens.
  const markerData: AggregatedPin[] = useMemo(() => {
    const byCoord: Record<string, AggregatedPin> = {};
    pins.forEach((pin) => {
      const key = `${pin.latitude},${pin.longitude}`;
      const existing = byCoord[key];
      if (existing) {
        existing.totalPassages += pin.passages || 0;
        existing.entries.push(pin);
      } else {
        byCoord[key] = {
          lat: pin.latitude,
          lng: pin.longitude,
          totalPassages: pin.passages || 0,
          entries: [pin],
        };
      }
    });
    return Object.values(byCoord);
  }, [pins]);

  const { center, zoom } = useMemo(() => {
    if (!markerData.length) return { center: BRAZIL_CENTER, zoom: 5 };
    const lat = markerData.reduce((sum, p) => sum + p.lat, 0) / markerData.length;
    const lng = markerData.reduce((sum, p) => sum + p.lng, 0) / markerData.length;
    return { center: { lat, lng }, zoom: markerData.length === 1 ? 10 : 7 };
  }, [markerData]);

  const simpleTheme = useMemo(() => createSimpleTheme(t), [t]);

  return (
    <Card className="padding-y-25 padding-x-15 padding-x-25-sm height-450 display-flex flex-column">
      <style>{MAP_TIP_STYLE}</style>
      <div className="display-flex flex-column gap-2 padding-bottom-20">
        <span className="text-size-20 text-bold">{t("tolls.map.concentrationTitle")}</span>
        <span className="text-size-14 text-color-gray">{t("tolls.map.subtitle")}</span>
      </div>

      <div className="flex-1-1 width-100pct" style={{ borderRadius: 4, overflow: "hidden" }}>
        <Map
          credentials={{ apikey: API_KEY }}
          language="pt-BR"
          center={center}
          zoom={zoom}
          maxZoom={22}
          showCluster={false}
          mapSettings={
            <MapSettings
              options={[
                <MapTypeSettings
                  key="mapTypeSettings"
                  tooltip={t("tolls.map.changeMapType")}
                  dropdownHeaderText={t("tolls.map.viewType")}
                  defaultTypeLabel={t("tolls.map.standardView")}
                  truckTypeLabel={t("tolls.map.fleetView")}
                  terrainTypeLabel={t("tolls.map.terrainView")}
                  satelliteTypeLabel={t("tolls.map.satelliteView")}
                  nightTypeLabel={t("tolls.map.nightView")}
                />,
              ]}
            />
          }
        >
          <MarkerLayer data={markerData} simpleTheme={simpleTheme} />
        </Map>
      </div>

      <div className="display-flex justify-content-between align-items-center padding-top-15 text-size-12 text-color-gray">
        <div className="display-flex flex-wrap align-items-center gap-10 gap-15-sm text-size-14">
          <span>{t("tolls.map.volumeLabel")}</span>
          {VOLUME_LEGEND.map(({ color, label }) => (
            <span key={color} className="display-flex align-items-center">
              <VolumeIndicator color={VOLUME_LINE_COLORS[color]} />
              <span className="margin-left-3">{label}</span>
            </span>
          ))}
        </div>
      </div>
    </Card>
  );
}
